import { Injectable } from 'angular-ts-decorators';

import { SpynlBase } from '@shared/app/scripts/services/spynl-base';
import { Domain } from '@shared/app/scripts/services/domain';
import type { IConfig } from '@shared/app/scripts/services/config';
import type { IStandardErrorParser } from '@shared/app/scripts/services/standard-error-parser';
import { CashierModel } from '@account-management/app/scripts/classes/cashier-model';

import { each, indexOf, isEmpty, keys, reduce } from 'lodash';
import { CashierAclModel } from '@classes/cashier-acl-model';

/**
 * Factory in the sharedModule. It takes care of communication with cashier
 * resource. Methods are inherited form {@link shared.spynlBase
 * spynlBase} service, and are documented there.
 *
 */
@Injectable('cashier')
export class Cashier extends SpynlBase<Object> {
  protected location: string;
  private $translate: ng.translate.ITranslateService;

  constructor(
    private translate: ng.translate.ITranslateService,
    private md5,
    protected $http: ng.IHttpService,
    protected $q: ng.IQService,
    protected sentry,
    protected domain: Domain,
    protected config: IConfig,
    protected standardErrorParser: IStandardErrorParser,
  ) {
    'ngInject';

    super($http, $q, sentry, domain, config, standardErrorParser, Object);

    this.location = '/cashiers';
    this.$translate = translate;
  }

  /*
   * Helper method used to assign the ACLs to a cashier depending on if the
   * cashier is a manager or a non-managerial cashier.
   */
  private assignAcls(
    cashierObject: CashierModel,
    acls: string[],
    role: string,
  ) {
    const managerAcls = [
      'pos_menu_acl',
      'pos_menu_dashboard',
      'pos_menu_discount_settings',
      'pos_menu_reasoneditor',
      'pos_menu_settings',
      'pos_menu_sscms',
    ];

    cashierObject.acls = reduce(
      acls,
      (sum, value) => {
        sum[value] = role === '' ? false : true;
        return sum;
      },
      {},
    );

    if (role !== 'manager') {
      each(managerAcls, (acl) => {
        cashierObject.acls[acl] = false;
      });
    }
  }

  /*
   * Helper function which checks if a passcode is in use by another cashier
   */
  private isPasscodeUnique(hash: string, tenantId: string) {
    const passcodeFilter = { password_hash: hash };

    return this.count(passcodeFilter, tenantId).then((response) => {
      if (response > 0) {
        return this.$q.reject(
          this.$translate.instant('validation.passcode_already_used'),
        );
      } else {
        // There are no other cashiers with this passcode, so it can be used
        return this.$q.resolve(
          this.$translate.instant('validation.passcode_is_ok'),
        );
      }
    });
  }

  /**
   *
   * Adds a new cashier and sets the passcode hash as well as the ACL access
   * level.
   * @param {object} cashierObject
   * Id of the cashier object to be inserted
   * @param {string} passcode
   * The new passcode to be hashed
   * @param {string} role
   * The ACL role to apply to the cashier. Currently allowed: normal, manager
   * @param {string} tenantId
   * The tenantId of the cashier. Optional paramter.
   * @returns {promise} it will resolve with success message, or fail with
   * error description
   */
  newCashier(
    cashierObject: CashierModel,
    passcode: string,
    role: string,
    tenantId: string,
  ): ng.IPromise<any> {
    let allAcls;
    if (!angular.isObject(cashierObject)) {
      return this.$q.reject(
        this.$translate.instant('shared.parameter_incorrect_type', {
          type: 'Object',
        }),
      );
    }
    if (isEmpty(passcode)) {
      return this.$q.reject(
        this.$translate.instant('shared.missing_reqired_parameter', {
          param: 'passcode',
        }),
      );
    }
    if (indexOf(['', 'normal', 'manager'], role) === -1) {
      return this.$q.reject(
        this.$translate.instant('shared.unsupported_role', { role: role }),
      );
    }

    // Set the Acls
    allAcls = keys(new CashierAclModel());
    this.assignAcls(cashierObject, allAcls, role);

    // Set active
    cashierObject.active = true;

    // Set the passcode hash
    cashierObject.password_hash = this.md5(passcode);

    return this.isPasscodeUnique(cashierObject.password_hash, tenantId).then(
      () => this.add(cashierObject, tenantId),
    );
  }

  /**
   *
   * Set the cashier's pass code. The pass code is used on the POS's PIN pad.
   * This method uses a simple md5 hash to create a passcode. The passcode
   * cannot be used by multiple cashiers. So each passcode must be unique.
   * @param {string} cashierId
   * Id of the cashier to be updated
   * @param {string} newPasscode
   * The new passcode to be set
   * @param {string} newPasscodeConfirmation
   * The confirmation of the new passcode to be set
   * @param {string} tenantId
   * The tenantId of the cashier. Optional paramter.
   * @returns {promise} it will resolve with success message, or fail with
   * error description
   */
  setPasscode(
    cashierId: string,
    newPasscode: string,
    newPasscodeConfirmation: string,
    tenantId: string,
  ): ng.IPromise<any> {
    let hash;
    if (!angular.isString(cashierId)) {
      // TODO make text translatable
      return this.$q.reject(
        this.$translate.instant('shared.entity_id_not_defined'),
      );
    }

    if (isEmpty(newPasscode)) {
      return this.$q.reject(
        this.$translate.instant('validation.passcode_cannot_be_empty'),
      );
    }

    if (newPasscode !== newPasscodeConfirmation) {
      return this.$q.reject(
        this.$translate.instant('validation.passcode_mismatch'),
      );
    }

    hash = this.md5(newPasscode);

    // Check if any other cashier is using this pass code
    return this.isPasscodeUnique(hash, tenantId).then(() =>
      this.update(cashierId, { $set: { password_hash: hash } }, tenantId),
    );
  }
}
